import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEye, faPaperPlane, faPen, faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../../../../shared/spinner';
import NotificacaoSmtpService, {
  NotificacaoEmailTemplateDTO,
  NotificacaoSmtpConfigDTO,
  StatusGestaoPromotoraOption,
} from '../../../../services/notificacaoSmtpService';

export interface NotificacaoSmtpCardRef {
  salvarConfiguracao: () => Promise<void>;
}

const service = new NotificacaoSmtpService();

const HTML_PADRAO = `<html>
  <body style="font-family: Arial, sans-serif; color: #222;">
    <h2>Gestão Promotora - {{statusDescricao}}</h2>
    <p><strong>Promotora:</strong> {{promotora.id}} - {{promotora.nome}}</p>
    <p><strong>CNPJ:</strong> {{promotora.cnpj}}</p>
    <p><strong>Usuário:</strong> {{usuario}}</p>
    <p><strong>Data/Hora:</strong> {{dataHora}}</p>
    <p><strong>Observação:</strong> {{observacao}}</p>
  </body>
</html>`;

const configInicial: NotificacaoSmtpConfigDTO = {
  ativo: true,
  smtpPort: 587,
  smtpAuth: true,
  smtpStarttls: true,
  gestaoPromotora: false,
};

const templateInicial: NotificacaoEmailTemplateDTO = {
  ativo: true,
  modulo: 'GESTAO_PROMOTORA',
  nome: '',
  enviarTodosStatus: true,
  statusGestao: '',
  assunto: 'Gestão Promotora - {{statusDescricao}} - {{promotora.nome}}',
  corpoHtml: HTML_PADRAO,
};

const NotificacaoSmtpCard = forwardRef<NotificacaoSmtpCardRef>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [config, setConfig] = useState<NotificacaoSmtpConfigDTO>(configInicial);
  const [templates, setTemplates] = useState<NotificacaoEmailTemplateDTO[]>([]);
  const [statusGestao, setStatusGestao] = useState<StatusGestaoPromotoraOption[]>([]);
  const [templateForm, setTemplateForm] = useState<NotificacaoEmailTemplateDTO>({ ...templateInicial });
  const [alerta, setAlerta] = useState<{ tipo: 'success' | 'danger'; texto: string } | null>(null);
  const [preview, setPreview] = useState<{ assunto: string; htmlPreVisualizacao: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const statusSelecionadoDescricao = useMemo(() => {
    return statusGestao.find((item) => item.value === templateForm.statusGestao)?.label || '-';
  }, [statusGestao, templateForm.statusGestao]);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const [configResp, templatesResp, statusResp] = await Promise.all([
        service.buscarConfiguracao(),
        service.listarTemplatesGestaoPromotora(),
        service.listarStatusGestaoPromotora(),
      ]);

      setConfig({ ...configInicial, ...(configResp.data || {}) });
      setTemplates(templatesResp.data || []);
      setStatusGestao(statusResp.data || []);
    } catch (error) {
      setAlerta({ tipo: 'danger', texto: 'Erro ao carregar notificações SMTP.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const atualizarConfig = (campo: keyof NotificacaoSmtpConfigDTO, valor: any) => {
    setConfig((atual) => ({ ...atual, [campo]: valor }));
  };

  const atualizarTemplate = (campo: keyof NotificacaoEmailTemplateDTO, valor: any) => {
    setTemplateForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const salvarConfiguracaoInterna = useCallback(async (exibirMensagem = true) => {
    try {
      setSalvando(true);
      const resp = await service.salvarConfiguracao(config);
      setConfig(resp.data);
      if (exibirMensagem) {
        setAlerta({ tipo: 'success', texto: 'Configuração SMTP salva com sucesso.' });
      }
    } catch (error: any) {
      setAlerta({ tipo: 'danger', texto: error?.response?.data || 'Erro ao salvar configuração SMTP.' });
      throw error;
    } finally {
      setSalvando(false);
    }
  }, [config]);

  useImperativeHandle(ref, () => ({
    salvarConfiguracao: async () => salvarConfiguracaoInterna(false),
  }), [salvarConfiguracaoInterna]);

  const salvarTemplate = async () => {
    try {
      setSalvando(true);
      await service.salvarTemplate({ ...templateForm, modulo: 'GESTAO_PROMOTORA' });
      setTemplateForm({ ...templateInicial });
      setAlerta({ tipo: 'success', texto: 'Template salvo com sucesso.' });
      await carregarDados();
    } catch (error: any) {
      setAlerta({ tipo: 'danger', texto: error?.response?.data || 'Erro ao salvar template.' });
    } finally {
      setSalvando(false);
    }
  };

  const editarTemplate = (template: NotificacaoEmailTemplateDTO) => {
    setTemplateForm({
      ...templateInicial,
      ...template,
      enviarTodosStatus: Boolean(template.enviarTodosStatus),
      statusGestao: template.statusGestao || '',
    });
  };

  const excluirTemplate = async (template: NotificacaoEmailTemplateDTO) => {
    if (!template.id || !window.confirm('Inativar este template?')) {
      return;
    }

    try {
      setSalvando(true);
      await service.excluirTemplate(template.id);
      setAlerta({ tipo: 'success', texto: 'Template inativado com sucesso.' });
      await carregarDados();
    } catch (error: any) {
      setAlerta({ tipo: 'danger', texto: error?.response?.data || 'Erro ao inativar template.' });
    } finally {
      setSalvando(false);
    }
  };

  const gerarPreview = async () => {
    try {
      const resp = await service.preVisualizarTemplate({ ...templateForm, modulo: 'GESTAO_PROMOTORA' });
      setPreview(resp.data);
      setShowPreview(true);
    } catch (error: any) {
      setAlerta({ tipo: 'danger', texto: error?.response?.data || 'Erro ao gerar pré-visualização.' });
    }
  };

  const testarConfiguracao = async () => {
    try {
      setSalvando(true);
      const resp = await service.testarConfiguracao(config);
      setAlerta({ tipo: 'success', texto: resp.data || 'E-mail de teste enviado com sucesso.' });
    } catch (error: any) {
      setAlerta({ tipo: 'danger', texto: error?.response?.data || 'Erro ao testar configuração SMTP.' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Spinner loading={loading || salvando} />

      {alerta && (
        <Alert variant={alerta.tipo} dismissible onClose={() => setAlerta(null)}>
          {alerta.texto}
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light fw-bold">
          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
          SMTP
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={2}>
              <Form.Check
                type="switch"
                label="Ativo"
                checked={Boolean(config.ativo)}
                onChange={(e) => atualizarConfig('ativo', e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="checkbox"
                label="Gestão Promotora"
                checked={Boolean(config.gestaoPromotora)}
                onChange={(e) => atualizarConfig('gestaoPromotora', e.target.checked)}
              />
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>E-mails de destino para teste</Form.Label>
                <Form.Control
                  value={config.emailsDestino || ''}
                  onChange={(e) => atualizarConfig('emailsDestino', e.target.value)}
                  placeholder="teste@empresa.com.br; validacao@empresa.com.br"
                />
                <Form.Text className="text-muted">
                  Nas notificações reais da gestão, o envio usa o e-mail cadastrado na promotora.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end justify-content-end gap-2">
              <Button type="button" variant="outline-secondary" onClick={testarConfiguracao}>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Testar
              </Button>
              <Button type="button" id="App-button-cor-principal-escura" onClick={() => salvarConfiguracaoInterna()}>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salvar
              </Button>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Host SMTP</Form.Label>
                <Form.Control
                  value={config.smtpHost || ''}
                  onChange={(e) => atualizarConfig('smtpHost', e.target.value)}
                  placeholder="smtp.empresa.com.br"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Porta</Form.Label>
                <Form.Control
                  type="number"
                  value={config.smtpPort || ''}
                  onChange={(e) => atualizarConfig('smtpPort', Number(e.target.value || 587))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>E-mail remetente</Form.Label>
                <Form.Control
                  value={config.emailRemetente || ''}
                  onChange={(e) => atualizarConfig('emailRemetente', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Nome remetente</Form.Label>
                <Form.Control
                  value={config.nomeRemetente || ''}
                  onChange={(e) => atualizarConfig('nomeRemetente', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Usuário SMTP</Form.Label>
                <Form.Control
                  value={config.smtpUsername || ''}
                  onChange={(e) => atualizarConfig('smtpUsername', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Senha SMTP</Form.Label>
                <Form.Control
                  type="password"
                  value={config.smtpPassword || ''}
                  onChange={(e) => atualizarConfig('smtpPassword', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="Autenticação"
                checked={Boolean(config.smtpAuth)}
                onChange={(e) => atualizarConfig('smtpAuth', e.target.checked)}
              />
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="STARTTLS"
                checked={Boolean(config.smtpStarttls)}
                onChange={(e) => atualizarConfig('smtpStarttls', e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {config.gestaoPromotora && (
        <Row className="g-3 mb-4">
          <Col lg={5}>
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-light fw-bold">Template</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Nome</Form.Label>
                      <Form.Control
                        value={templateForm.nome || ''}
                        onChange={(e) => atualizarTemplate('nome', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      label="Ativo"
                      checked={Boolean(templateForm.ativo)}
                      onChange={(e) => atualizarTemplate('ativo', e.target.checked)}
                    />
                  </Col>
                  <Col md={5}>
                    <Form.Check
                      type="radio"
                      name="escopoStatusTemplate"
                      label="Todos os status"
                      checked={Boolean(templateForm.enviarTodosStatus)}
                      onChange={() => setTemplateForm((atual) => ({ ...atual, enviarTodosStatus: true, statusGestao: '' }))}
                    />
                    <Form.Check
                      type="radio"
                      name="escopoStatusTemplate"
                      label="Status específico"
                      checked={!templateForm.enviarTodosStatus}
                      onChange={() => setTemplateForm((atual) => ({ ...atual, enviarTodosStatus: false }))}
                    />
                  </Col>
                  <Col md={7}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        disabled={Boolean(templateForm.enviarTodosStatus)}
                        value={templateForm.statusGestao || ''}
                        onChange={(e) => atualizarTemplate('statusGestao', e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {statusGestao.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Assunto</Form.Label>
                      <Form.Control
                        value={templateForm.assunto || ''}
                        onChange={(e) => atualizarTemplate('assunto', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>HTML</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={14}
                        value={templateForm.corpoHtml || ''}
                        onChange={(e) => atualizarTemplate('corpoHtml', e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: '13px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-3">
                  <Button type="button" variant="outline-secondary" onClick={() => setTemplateForm({ ...templateInicial })}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Novo
                  </Button>
                  <div>
                    <Button type="button" variant="outline-dark" className="me-2" onClick={gerarPreview}>
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Pré-visualizar
                    </Button>
                    <Button type="button" id="App-button-cor-principal-escura" onClick={salvarTemplate}>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Salvar template
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-light fw-bold">Templates cadastrados</Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table bordered striped hover size="sm" className="align-middle">
                    <thead>
                      <tr>
                        <th id="App-header-table">Nome</th>
                        <th id="App-header-table">Notificação</th>
                        <th id="App-header-table">Status</th>
                        <th id="App-header-table" style={{ width: 90 }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template) => (
                        <tr key={template.id}>
                          <td>{template.nome}</td>
                          <td>{template.enviarTodosStatus ? 'Todos os status' : template.statusGestaoDescricao}</td>
                          <td>
                            <Badge bg={template.ativo ? 'success' : 'secondary'}>
                              {template.ativo ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </td>
                          <td>
                            <Button type="button" variant="outline-primary" size="sm" className="me-1" onClick={() => editarTemplate(template)}>
                              <FontAwesomeIcon icon={faPen} />
                            </Button>
                            <Button type="button" variant="outline-danger" size="sm" onClick={() => excluirTemplate(template)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {templates.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">Nenhum template cadastrado.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Pré-visualização HTML</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>Assunto:</strong> {preview?.assunto || templateForm.assunto}
          </div>
          <div className="mb-2">
            <strong>Notificação:</strong> {templateForm.enviarTodosStatus ? 'Todos os status' : statusSelecionadoDescricao}
          </div>
          <iframe
            title="Pré-visualização do e-mail"
            srcDoc={preview?.htmlPreVisualizacao || ''}
            sandbox=""
            style={{ width: '100%', minHeight: '520px', border: '1px solid #dee2e6', borderRadius: 4 }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setShowPreview(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default NotificacaoSmtpCard;
